import React, { useState, useEffect } from 'react';
import { Users, Circle, Clock } from 'lucide-react';
import { presenceService } from '../services/presenceService';
import { ActiveUsersData } from '../types/Presence';

interface ActiveUsersWidgetProps {
  onUserClick?: (userId: string) => void;
}

const ActiveUsersWidget: React.FC<ActiveUsersWidgetProps> = ({ onUserClick }) => {
  const [activeData, setActiveData] = useState<ActiveUsersData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Subscribe to real-time updates
    const unsubscribe = presenceService.subscribeToActiveUsers(
      (data) => {
        setActiveData(data);
        setLoading(false);
      },
      (error) => {
        console.error('Failed to subscribe to active users:', error);
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const getTimeAgo = (dateString: string): string => {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 120) return '1 minute ago';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 7200) return '1 hour ago';
    return `${Math.floor(seconds / 3600)} hours ago`;
  };

  if (loading) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '1.5rem',
        borderRadius: '8px',
        color: 'white',
        minHeight: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="animate-spin" style={{ width: '24px', height: '24px' }}>
          <Circle size={24} />
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '1.5rem',
      borderRadius: '8px',
      color: 'white',
      boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div style={{ position: 'relative' }}>
          <Users size={32} />
          {activeData && activeData.activeCount > 0 && (
            <div style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              width: '12px',
              height: '12px',
              background: '#10b981',
              borderRadius: '50%',
              border: '2px solid white',
              animation: 'pulse 2s infinite'
            }} />
          )}
        </div>
        <div>
          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Players Online Now</div>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', lineHeight: 1 }}>
            {activeData?.activeCount || 0}
          </div>
        </div>
      </div>

      {activeData && activeData.users.length > 0 && (
        <div style={{
          marginTop: '1rem',
          paddingTop: '1rem',
          borderTop: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>
            Recently Active:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '150px', overflowY: 'auto' }}>
            {activeData.users.slice(0, 5).map((user) => (
              <div
                key={user.userId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  background: 'rgba(255, 255, 255, 0.1)',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  cursor: onUserClick ? 'pointer' : 'default',
                  transition: 'background 0.2s'
                }}
                onClick={() => onUserClick?.(user.userId)}
                onMouseEnter={(e) => {
                  if (onUserClick) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                <Circle size={8} fill="#10b981" color="#10b981" />
                <span style={{ fontWeight: '500', flex: 1, textDecoration: onUserClick ? 'underline' : 'none' }}>
                  {user.userName || `User ${user.userId.substring(0, 8)}`}
                </span>
                {user.currentLevel && user.currentLevel > 0 && (
                  <span style={{ opacity: 0.8, fontSize: '0.75rem' }}>
                    Level {user.currentLevel}
                  </span>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', opacity: 0.7, fontSize: '0.75rem' }}>
                  <Clock size={12} />
                  {getTimeAgo(user.lastSeen)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{
        marginTop: '1rem',
        fontSize: '0.75rem',
        opacity: 0.7,
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem'
      }}>
        <Clock size={12} />
        Updated {activeData ? getTimeAgo(activeData.lastUpdated) : 'never'}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.2);
          }
        }
      `}</style>
    </div>
  );
};

export default ActiveUsersWidget;
